/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import template from './creative-preview-view.component.html';
import {
  collateCreativePreviewLineItemData,
  collateCreativePreviewRequestData
} from './preview-request-data';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {requiresBlankPagePreview, requiresVideoPreviewParam} from './preview-language';
import {CreativePreviewApiService} from '../creative-preview-api.service';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';

@Component({
  standalone: false,
  selector: 'adn-creative-preview-via-layout',
  template
})
export class CreativePreviewViaLayoutComponent implements OnChanges, OnInit {
  @Input() previewData: any;
  @Input() creative: any;
  @Input() creativePreviewTrigger: any;
  @Input() maxWidth: string;
  @Input() maxHeight: string;

  previewTrigger: any = {};
  readonly resolved = signal(false);
  readonly previewError = signal(false);
  readonly layoutType = signal<string | undefined>(undefined);

  private lastPreviewParams: any;
  private initialized = false;
  private previewVersion = 0;

  constructor(
    private readonly creativePreviewApi: CreativePreviewApiService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.initialized = true;
    this.runPreview(this.previewData);

    if (this.creativePreviewTrigger) {
      this.creativePreviewTrigger.previewCreative = (params: any) => {
        this.runPreview(params);
      };
    }

    this.previewTrigger.refreshPreview = (options: any) => {
      if (!this.lastPreviewParams) {
        return;
      }
      options = _.isObject(options) ? options : {hasUserTargeting: options};
      this.runPreview(_.assign({}, this.lastPreviewParams, {
        hasUserTargetingOverride: options.hasUserTargeting,
        previewCreativeWidth: options.creativeWidth,
        previewCreativeHeight: options.creativeHeight
      }));
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized || (!changes.creative && !changes.previewData)) {
      return;
    }
    this.runPreview(this.previewData);
  }

  private runPreview(params: any): void {
    const previewVersion = ++this.previewVersion;
    this.lastPreviewParams = params ? _.omit(params, ['hasUserTargetingOverride', 'previewCreativeWidth', 'previewCreativeHeight']) : null;
    this.resolved.set(false);
    this.previewError.set(false);

    if (!params || (!params.layoutId && !params.renderTemplate && !params.creative)) {
      this.resolved.set(true);
      return;
    }

    const creative = params.creative;
    const lineItem = params.lineItem || params;
    const layoutType = _.get(creative, ['layout', 'layoutType']) || '';
    const ogLayoutType = _.get(creative, ['layout', 'ogLayoutType']) || '';
    const promises: any[] = [];

    const layoutParams: any = {};
    const totalAssets = _.get(creative, ['assets'], []).concat(_.get(creative, ['libraryCreativeAssets'], []));
    _.forEach(creative.layoutParameters, (v: any, k: string) => {
      if (v && v.id && !v.content) {
        const assetId = v.id;
        layoutParams[k] = _.pick((_.find(totalAssets, ['id', assetId]) || {}),
          ['cdnId', 'previewCdnId', 'transcode', 'width', 'height', 'content', 'duration', 'bps']);
      } else {
        layoutParams[k] = v;
      }
    });

    this.layoutType.set(ogLayoutType || layoutType || _.get(creative, ['layout', 'detectedCategory']));
    const creativeWidth = this.getPreviewDimension(params.previewCreativeWidth, creative.width, creative.height);
    const creativeHeight = this.getPreviewDimension(params.previewCreativeHeight, creative.height, creative.width);
    const apiPreviewData: any = {
      creativeWidth,
      creativeHeight,
      layoutParameters: layoutParams,
      video: _.isUndefined(params.video) ? requiresVideoPreviewParam(creative) : params.video
    };
    _.assign(apiPreviewData, collateCreativePreviewLineItemData(creative, lineItem));
    _.assign(apiPreviewData, collateCreativePreviewRequestData(
      creative,
      lineItem,
      params.advertiser,
      params.election,
      this.localNetworkInfo
    ));
    if (!_.isUndefined(params.hasUserTargetingOverride)) {
      apiPreviewData.hasUserTargeting = params.hasUserTargetingOverride;
    }
    if (params.layoutId) {
      apiPreviewData.layoutId = params.layoutId;
    }
    if (_.get(creative, ['layout', 'id'])) {
      apiPreviewData.layoutId = creative.layout.id;
    }
    if (params.renderTemplate) {
      apiPreviewData.renderTemplate = params.renderTemplate;
    }

    if (!apiPreviewData.layoutParameters || _.isEmpty(apiPreviewData.layoutParameters)) {
      promises.push(Promise.resolve(''));
    } else {
      promises.push(firstValueFrom(this.creativePreviewApi.render(apiPreviewData)).then((data: any) => data.document));
    }

    Promise.all(promises).then((results: any[]) => {
      if (previewVersion !== this.previewVersion) {
        return;
      }
      const creativeContent = results[0] || '\x3Chtml>\x3Cbody>\x3C/body>\x3C/html>';
      const previewPayload = {
        pageTitle: params.renderTemplate ? 'Full-Screen Layout Preview' : 'Full-Screen Creative Preview',
        creativeContent,
        creativeWidth,
        creativeHeight,
        dimensionType: creative.dimensionType,
        layoutType: ogLayoutType || layoutType,
        maxWidth: this.maxWidth,
        maxHeight: this.maxHeight,
        center: true
      };

      const blankPageOnly = requiresBlankPagePreview(creative);
      deferSignalStateWrite(() => {
        if (previewVersion !== this.previewVersion) {
          return;
        }
        this.previewTrigger.previewInline?.(previewPayload, apiPreviewData, blankPageOnly);
        this.resolved.set(true);
      });
    }).catch(() => {
      if (previewVersion !== this.previewVersion) {
        return;
      }
      deferSignalStateWrite(() => {
        if (previewVersion !== this.previewVersion) {
          return;
        }
        this.previewError.set(true);
        this.resolved.set(true);
      });
    });
  }

  private getPreviewDimension(overrideValue: any, primaryValue: any, fallbackValue: any): number {
    return Number(overrideValue) || Number(primaryValue) || Number(fallbackValue) || 300;
  }
}
